import { useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, BookOpen, Download } from 'lucide-react';
import SoapNote from '../components/SoapNote';
import NoteAddendumsList from '../components/NoteAddendumsList';
import { NOTE_TYPE_LABELS } from '../constants/schemas';
import { getNoteTypeLabel } from '../utils/noteTypeLabels';
import { useSession } from '../contexts/SessionContext';
import { useLanguage } from '../contexts/LanguageContext';
import { generatePDF } from '../services/pdfGenerator';
import { ROUTES } from '../constants/routes';
import { getNoteDetailRoute } from '../utils/noteDetailRoutes';
import NoteTimingMeta from '../components/NoteTimingMeta';
import { resolveNoteTiming } from '../utils/lateEntry';
import { getNoteAddendums } from '../utils/addendum';

export default function NoteDetailScreen() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { sessionNotes } = useSession();
  const { language, t } = useLanguage();
  const note = location.state?.note
    ? sessionNotes.find((n) => n.id === location.state.note.id) || location.state.note
    : sessionNotes.find((n) => n.id === id);
  const content = note?.openaiNote || note?.note_content;

  useEffect(() => {
    if (!note) return;
    if (note.note_type === 'progress_note') return;
    const redirect = getNoteDetailRoute(note);
    if (redirect) {
      navigate(redirect.path, { replace: true, state: redirect.state });
      return;
    }
    navigate(ROUTES.NOTES, { replace: true });
  }, [navigate, note]);

  if (!note || note.note_type !== 'progress_note') {
    return (
      <div className="flex min-h-screen items-center justify-center text-gray-500">
        {t('noNotesYet')}
      </div>
    );
  }

  const noteType = note.note_type;
  const datetime = new Date(note.datetime);
  const timing = resolveNoteTiming(note);

  const handlePdf = () => {
    generatePDF({
      note: content,
      noteType,
      patientName: note.patient_name || 'Unknown',
      room: note.patient_room || '—',
      datetime,
      careTime: timing.careTime,
      isLateEntry: timing.isLateEntry,
      addendums: getNoteAddendums(note),
    });
  };

  return (
    <div className="min-h-screen bg-page pb-28">
      <header className="sticky top-0 z-30 border-b border-gray-200 bg-surface px-4 py-3">
        <button
          type="button"
          onClick={() => navigate(ROUTES.NOTES)}
          className="flex items-center gap-2 text-primary"
        >
          <ArrowLeft className="h-5 w-5" />
          {t('back')}
        </button>
        <h1 className="mt-2 text-lg font-bold text-gray-900">{note.patient_name}</h1>
        <div className="mt-1 flex items-center gap-1.5 text-sm font-medium text-gray-700">
          <BookOpen className="h-4 w-4 text-primary" strokeWidth={2} aria-hidden="true" />
          <span>
            {getNoteTypeLabel(noteType, language) || NOTE_TYPE_LABELS[noteType]}
          </span>
        </div>
        <NoteTimingMeta note={note} className="mt-1" />
      </header>

      <div className="px-4 py-4">
        <SoapNote
          note={content}
          onChange={() => {}}
          datetime={datetime}
          hideExtractedVitals
          readOnly
        />
        <NoteAddendumsList note={note} />
      </div>

      <div className="fixed bottom-0 left-0 right-0 flex items-center justify-center border-t border-gray-200 bg-surface p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
        <button
          type="button"
          onClick={handlePdf}
          className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-white"
          aria-label="Download PDF"
        >
          <Download className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}
